<template>
  <div data-testid="more-destinations-view" class="more-destinations">
    <section
      v-if="loading"
      class="rewards-signup-banner rewards-signup-banner--loading"
      aria-hidden="true"
    >
      <div class="rewards-signup-banner__inner">
        <div class="rewards-signup-banner__content">
          <div class="rewards-signup-banner__skeleton rewards-signup-banner__skeleton--title" />
          <div class="rewards-signup-banner__skeleton rewards-signup-banner__skeleton--sub" />
          <div class="rewards-signup-banner__skeleton rewards-signup-banner__skeleton--cta" />
        </div>

        <div class="rewards-signup-banner__media">
          <div class="rewards-signup-banner__frame">
            <div class="rewards-signup-banner__skeleton rewards-signup-banner__skeleton--image" />
          </div>
        </div>
      </div>
    </section>

    <section v-else-if="selectedBannerNormalized" class="rewards-signup-banner">
      <div
        class="rewards-signup-banner__inner"
        :class="{ 'rewards-signup-banner__inner--light-text': selectedBannerNormalized.textColor }"
        :style="backgroundStyle"
        data-testid="smart-banner-0"
      >
        <div class="rewards-signup-banner__content">
          <h2 v-if="selectedBannerNormalized.title" class="rewards-signup-banner__title">
            {{ selectedBannerNormalized.title }}
          </h2>

          <p v-if="selectedBannerNormalized.subtitle" class="rewards-signup-banner__subtitle">
            {{ selectedBannerNormalized.subtitle }}
          </p>

          <button
            v-if="selectedBannerNormalized.ctaTitle"
            type="button"
            class="rewards-signup-banner__cta"
            :style="ctaStyle"
            :data-toggle="!authenticated ? 'modal' : null"
            :data-target="!authenticated ? '#modal-loyalty-main-navigation' : null"
            data-testid="smart-banner-cta-0"
            @click="onCtaClick(selectedBannerNormalized)"
          >
            {{ selectedBannerNormalized.ctaTitle }}
            <i v-if="authenticated" class="rhg-font-icon--external-link"></i>
          </button>
        </div>

        <div v-if="bannerImageSrc" class="rewards-signup-banner__media">
          <img
            :src="bannerImageSrc"
            :alt="selectedBannerNormalized.imageAlt"
            class="rewards-signup-banner__frame"
          />
        </div>
      </div>
    </section>
    <CrossDomainTransitionModal
      :visible="showCrossDomainModal"
      :href="decoratedHref || href"
      :source-brand="sourceBrand"
      :image-src="modalImageSrc"
      @close="showCrossDomainModal = false"
      @confirm="handleModalConfirm"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import {
  crossDomainUrl,
  LIGHTBOX_TIMEOUT_CONFIG_KEY,
  DEFAULT_LIGHTBOX_TIMEOUT_SECONDS
} from '@/infrastructure/Configuration'
import {
  buildImageUrl,
  buildCid,
  decorateUrlWithCid,
  trackUtagAsync,
  normalizeSourceBrand
} from '@/infrastructure/Utils'
import CrossDomainTransitionModal from '@/components/common/CrossDomainTransitionModal.vue'

const { mapGetters, mapActions } = createNamespacedHelpers('offersDestinations')

const LOGIN_MODAL_SELECTOR = '#modal-loyalty-main-navigation'

export default {
  name: 'MoreDestinations',

  components: {
    CrossDomainTransitionModal
  },

  props: {
    href: {
      type: String,
      default: crossDomainUrl()
    }
  },

  data() {
    return {
      dataReady: false,
      authState: null,
      authEventHandler: null,
      noAuthEventHandler: null,
      showCrossDomainModal: false,
      decoratedHref: ''
    }
  },

  computed: {
    ...mapGetters(['moreDestinationsBanners', 'modalSmartLinkImages']),

    loading() {
      return !this.dataReady
    },

    authenticated() {
      if (this.authState !== null) {
        return this.authState
      }

      return this.getCurrentAuthState()
    },

    selectedBanner() {
      const banners = this.moreDestinationsBanners || {}
      return this.authenticated ? banners.logged : banners.nonLogged
    },

    selectedBannerNormalized() {
      if (!this.selectedBanner) {
        return null
      }

      return this.normalizeBanner(this.selectedBanner)
    },

    backgroundStyle() {
      if (!this.selectedBannerNormalized || !this.selectedBannerNormalized.backgroundColor) {
        return {}
      }

      return {
        backgroundColor: `#${this.selectedBannerNormalized.backgroundColor}`
      }
    },

    ctaStyle() {
      if (!this.selectedBannerNormalized) {
        return {}
      }

      if (
        this.selectedBannerNormalized.textColor &&
        this.selectedBannerNormalized.backgroundColor
      ) {
        return {
          color: `#${this.selectedBannerNormalized.backgroundColor}`,
          '--cta-hover-color': `#${this.selectedBannerNormalized.backgroundColor}`
        }
      }

      return { color: 'white' }
    },

    bannerImageSrc() {
      if (
        this.selectedBannerNormalized &&
        this.selectedBannerNormalized.raw &&
        this.selectedBannerNormalized.raw.image
      ) {
        return buildImageUrl(this.selectedBannerNormalized.raw.image, 'Card')
      }

      return ''
    },

    sourceBrand() {
      try {
        const hostname =
          typeof window !== 'undefined' && window.location && window.location.hostname
            ? window.location.hostname.toLowerCase()
            : ''

        if (hostname.includes('louvre') || hostname.includes('louvrehotels')) {
          return 'lhg'
        }
      } catch (e) {}

      return 'rhg'
    },

    modalImageSrc() {
      if (
        this.modalSmartLinkImages &&
        this.sourceBrand &&
        this.modalSmartLinkImages[this.sourceBrand]
      ) {
        let val = this.modalSmartLinkImages[this.sourceBrand]

        if (typeof val === 'string') {
          val = { url: val }
        }

        try {
          return buildImageUrl(val, 'Card')
        } catch (e) {
          return val && val.url ? val.url : ''
        }
      }

      return ''
    }
  },

  watch: {
    authenticated(newVal, oldVal) {
      if (newVal === oldVal) {
        return
      }

      this.dataReady = false

      this.preloadCurrentBannerImage().finally(() => {
        this.dataReady = true
      })
    }
  },

  async created() {
    this.authState = this.getCurrentAuthState()

    await this.loadBannerData()
  },

  mounted() {
    this.bindAuthEvents()
  },

  beforeDestroy() {
    this.unbindAuthEvents()
  },

  methods: {
    ...mapActions(['fetchMoreDestinationsBanners', 'fetchModalSmartLinkImages']),

    getCurrentAuthState() {
      try {
        const bookingSummaryAuth = this.$store.getters['bookingSummary/isAuthenticated']

        if (bookingSummaryAuth !== undefined && bookingSummaryAuth !== null) {
          return bookingSummaryAuth
        }
      } catch (e) {
        console.error('MoreDestinations: bookingSummary/isAuthenticated getter not available', e)
      }

      return global.Cookie && global.Cookie.read ? global.Cookie.read('auth') === 'true' : false
    },

    getLightboxTimeoutSeconds() {
      const raw =
        global.ConfigDynamic && typeof global.ConfigDynamic.valueOf === 'function'
          ? global.ConfigDynamic.valueOf(LIGHTBOX_TIMEOUT_CONFIG_KEY)
          : undefined

      const parsed = parseInt(raw, 10)

      return Number.isFinite(parsed) ? parsed : DEFAULT_LIGHTBOX_TIMEOUT_SECONDS
    },

    normalizeBanner(banner) {
      return {
        imageUrl: banner.image && banner.image.url ? banner.image.url : '',
        imageAlt: banner.image && banner.image.altText ? banner.image.altText : '',
        title: banner.text && banner.text.title ? banner.text.title : '',
        subtitle: banner.text && banner.text.subtitle ? banner.text.subtitle : '',
        ctaTitle: banner.cta && banner.cta.title ? banner.cta.title : '',
        backgroundColor:
          banner.color && banner.color.backgroundColor && banner.color.backgroundColor.key
            ? banner.color.backgroundColor.key
            : '',
        textColor:
          banner.color && banner.color.textColor !== undefined ? banner.color.textColor : false,
        raw: banner
      }
    },

    preloadImage(src) {
      return new Promise(resolve => {
        if (!src || typeof window === 'undefined') {
          resolve()
          return
        }

        try {
          const image = new Image()

          image.onload = () => {
            resolve()
          }

          image.onerror = () => {
            console.error('MoreDestinations: image preload failed', src)
            resolve()
          }

          image.src = src
        } catch (e) {
          resolve()
        }
      })
    },

    async loadBannerData() {
      this.dataReady = false

      try {
        await this.fetchMoreDestinationsBanners()
        await this.fetchModalSmartLinkImages()
        await this.preloadCurrentBannerImage()
      } catch (e) {
        console.error('MoreDestinations: loadBannerData failed', e)
      } finally {
        this.dataReady = true
      }
    },

    preloadCurrentBannerImage() {
      return this.$nextTick().then(() => {
        const imagesToPreload = [this.bannerImageSrc, this.modalImageSrc]
          .filter(Boolean)
          .map(src => this.preloadImage(src))

        if (!imagesToPreload.length) {
          return Promise.resolve()
        }

        const timeout = new Promise(resolve => {
          setTimeout(resolve, this.getLightboxTimeoutSeconds() * 1000)
        })

        return Promise.race([Promise.all(imagesToPreload), timeout])
      })
    },

    buildOffersSmartLinkCid() {
      return buildCid({
        a: normalizeSourceBrand(this.sourceBrand),
        de: 'all',
        q: 'of'
      })
    },

    buildOffersSmartLinkPayload(cid) {
      return {
        tealium_event: 'exit_link_click',
        smart_link_type: 'offers',
        smart_link_location: 'all',
        exit_link_type: 'smart-link',
        cid
      }
    },

    async trackOffersSmartLink(payload) {
      await trackUtagAsync(payload)
    },

    async prepareOffersSmartLinkUrl() {
      const cid = this.buildOffersSmartLinkCid()
      const payload = this.buildOffersSmartLinkPayload(cid)

      await this.trackOffersSmartLink(payload)

      const decoratedUrl = decorateUrlWithCid(this.href, cid)

      return decoratedUrl
    },

    async onCtaClick() {
      if (this.authenticated) {
        this.decoratedHref = await this.prepareOffersSmartLinkUrl()

        this.showCrossDomainModal = true
        return
      }

      this.openLoginModal()
    },
    openLoginModal() {
      const $ = global.$ || window.$

      if ($ && $(LOGIN_MODAL_SELECTOR).length) {
        $(LOGIN_MODAL_SELECTOR).modal('show')
        return
      }

      console.warn('MoreDestinations: login modal not found', LOGIN_MODAL_SELECTOR)
    },

    refreshComponentAfterLogin() {
      this.authState = true
      this.preloadCurrentBannerImage()
    },

    refreshComponentAfterLogout() {
      this.authState = false
      this.preloadCurrentBannerImage()
    },

    bindAuthEvents() {
      const $ = global.$ || window.$

      if (!$) {
        console.warn('MoreDestinations: jQuery not available, auth events not bound')
        return
      }

      this.authEventHandler = () => {
        this.refreshComponentAfterLogin()
      }

      this.noAuthEventHandler = () => {
        this.refreshComponentAfterLogout()
      }

      $(window).on(
        'auth.logged.moreDestinations auth.postlogged.moreDestinations',
        this.authEventHandler
      )

      $(window).on('auth.nologged.moreDestinations', this.noAuthEventHandler)
    },

    unbindAuthEvents() {
      const $ = global.$ || window.$

      if (!$) {
        return
      }

      if (this.authEventHandler) {
        $(window).off(
          'auth.logged.moreDestinations auth.postlogged.moreDestinations',
          this.authEventHandler
        )
      }

      if (this.noAuthEventHandler) {
        $(window).off('auth.nologged.moreDestinations', this.noAuthEventHandler)
      }

      this.authEventHandler = null
      this.noAuthEventHandler = null
    },

    handleModalConfirm() {
      this.showCrossDomainModal = false
    }
  }
}
</script>

<style scoped lang="scss">
.more-destinations {
  width: 100%;
}

.rewards-signup-banner {
  width: 100%;

  &__inner {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 640px;
    overflow: hidden;
    border-radius: 8px;
    background: var(--color-light-grey);

    @media (min-width: 768px) {
      flex-direction: row;
      min-height: 396px;
    }
  }

  &__content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1.6rem;
    width: 100%;
    padding: 2.4rem;

    @media (min-width: 768px) {
      justify-content: center;
      width: 50%;
      min-height: 396px;
      padding: 4rem 2.4rem;
    }
  }

  &__title {
    margin: 0;
    color: var(--color-eclipse);
    font-size: 3.2rem;
    line-height: 3.8rem;
    font-weight: 400;

    @media (min-width: 768px) {
      max-width: 540px;
      font-size: 3.6rem;
      line-height: 4.4rem;
    }
  }

  &__subtitle {
    margin: 0;
    color: var(--color-eclipse);
    font-size: 1.8rem;
    line-height: 2.4rem;

    @media (min-width: 768px) {
      max-width: 520px;
      font-size: 2rem;
      line-height: 2.6rem;
    }
  }

  &__cta {
    display: flex;
    gap: 8px;
    align-self: stretch;
    width: 100%;
    padding: 13px 24px;
    border: 0;
    border-radius: 24px;
    background-color: var(--color-eclipse);
    color: var(--color-white);
    font-size: 1.6rem;
    line-height: 2.2rem;
    font-weight: 700;
    cursor: pointer;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: color-mix(in srgb, var(--color-eclipse) 80%, black);
    }

    @media (min-width: 768px) {
      align-self: flex-start;
      justify-content: flex-start;
      width: fit-content;
      min-width: 112px;
    }
  }

  &__media {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 310px;
    margin-top: 2rem;
    overflow: visible;

    @media (min-width: 768px) {
      position: absolute;
      top: 0;
      right: 0;
      left: auto;
      width: 54%;
      height: 100%;
      margin-top: 0;
      overflow: hidden;
    }
  }

  &__frame {
    position: absolute;
    top: 0;
    left: 50%;
    display: block;
    width: 520px;
    height: 520px;
    max-width: none;
    object-fit: cover;
    overflow: hidden;
    border-radius: 50%;
    transform: translateX(-50%);

    @media (min-width: 768px) {
      top: 50%;
      left: 10%;
      width: 760px;
      height: 760px;
      transform: translateY(-50%);
    }
  }

  &--loading {
    .rewards-signup-banner__inner {
      background: var(--color-grey-light);
    }
  }

  &__inner--light-text {
    .rewards-signup-banner__title,
    .rewards-signup-banner__subtitle {
      color: var(--color-white);
    }

    .rewards-signup-banner__cta {
      background-color: var(--color-white);
      &:hover {
        background-color: color-mix(
          in srgb,
          var(--color-white) 80%,
          var(--cta-hover-color, var(--color-eclipse))
        );
      }
    }
  }

  &__skeleton {
    background: var(--color-grey);
    border-radius: 4px;
    animation: rewards-signup-banner-pulse 1.5s ease-in-out infinite;

    &--title {
      width: 85%;
      height: 32px;
    }

    &--sub {
      width: 60%;
      height: 20px;
    }

    &--cta {
      width: 180px;
      height: 44px;
      border-radius: 24px;
    }

    &--image {
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }
  }
}

@keyframes rewards-signup-banner-pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }

  100% {
    opacity: 1;
  }
}
</style>
